import React, { useEffect, useState, useRef } from 'react';
import { Play, Loader2, Clock } from 'lucide-react';
import { publicApi, BACKEND_URL } from '../lib/api';

const CATEGORY_STYLES = {
  'REPORTAGE': 'bg-[#F4A340]/20 text-[#E89534] border border-[#F4A340]/40',
  'LE TERRITOIRE': 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  'BON PLAN': 'bg-sky-100 text-sky-700 border border-sky-200',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return ''; }
};

const CardPlayer = ({ card }) => {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const url = card.video_url && !card.video_url.startsWith('http') ? `${BACKEND_URL}${card.video_url}` : card.video_url;
  const play = () => {
    setPlaying(true);
    setTimeout(() => videoRef.current?.play().catch(() => {}), 50);
  };
  const isProcessing = card.processing;

  return (
    <div className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-shadow group" data-testid={`lagoon-card-${card.id}`}>
      <div className="relative aspect-[4/3] bg-slate-200 overflow-hidden">
        {isProcessing ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-100 text-slate-500 text-sm gap-2">
            <Loader2 className="w-8 h-8 animate-spin text-[#F4A340]" />
            Traitement de la vidéo…
          </div>
        ) : url ? (
          playing ? (
            <video
              ref={videoRef}
              src={url}
              poster={card.thumbnail_url || undefined}
              controls autoPlay
              className="w-full h-full object-cover"
            />
          ) : (
            <>
              {card.thumbnail_url ? (
                <img src={card.thumbnail_url} alt={card.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <video
                  src={url}
                  muted preload="metadata"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onLoadedData={(e) => { try { e.currentTarget.currentTime = 0.5; } catch {} }}
                />
              )}
              <button
                onClick={play}
                aria-label={`Lire ${card.title}`}
                className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors"
              >
                <div className="w-16 h-16 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                  <Play className="w-6 h-6 text-slate-900 ml-1" fill="currentColor" />
                </div>
              </button>
            </>
          )
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
            Vidéo à venir
          </div>
        )}
      </div>

      <div className="p-5">
        <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full ${CATEGORY_STYLES[card.category] || CATEGORY_STYLES['REPORTAGE']}`}>
          {card.category}
        </span>
        <h3 className="mt-3 text-xl font-bold text-slate-900 leading-tight">{card.title}</h3>
        {card.description && (
          <p className="mt-2 text-sm text-slate-500 line-clamp-2">{card.description}</p>
        )}
        <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
          <span>{formatDate(card.created_at)}</span>
          {card.duration_min ? (
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {card.duration_min} min de lecture</span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const LagoonCarnet = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi.getLagoonCards().then((c) => { setCards(c); setLoading(false); }).catch(() => setLoading(false));
    // Poll if any card is processing
    const t = setInterval(() => {
      publicApi.getLagoonCards().then((c) => setCards(c)).catch(() => {});
    }, 8000);
    return () => clearInterval(t);
  }, []);

  if (loading) return null;
  if (cards.length === 0) return null;

  return (
    <section id="carnet-lagune" className="bg-white py-20 px-6" data-testid="lagoon-carnet">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900">
            Notre <span className="text-[#E89534]">carte lagunaire</span>
          </h2>
          <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
            Ce qu'on raconte à bord, et un peu plus.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card) => <CardPlayer key={card.id} card={card} />)}
        </div>
      </div>
    </section>
  );
};

export default LagoonCarnet;
